/**
 * A call asked for before its session existed (RFC 0307 Design).
 *
 * The voice button in the new pane has no session to call on. It creates one,
 * marks it here, and opens it; the thread's composer takes the mark on mount
 * and starts the call, so the call lives where every later line belongs.
 */

import type { ComposerVoice } from './types'

const pending = new Set<string>()

export function startVoiceOnOpen(sessionId: string): void {
  pending.add(sessionId)
}

/** Whether a call was asked for on this session and has not started yet. */
export function hasVoiceOnOpen(sessionId: string): boolean {
  return pending.has(sessionId)
}

/** True once, for the session a call was asked for. */
export function takeVoiceOnOpen(sessionId: string): boolean {
  return pending.delete(sessionId)
}

/**
 * The voice button on a composer that has no session yet: the new pane and the
 * session list. Never 'connecting', which would draw a live call's controls;
 * the call itself starts in the session's own composer once it opens.
 */
export function voiceBeforeSession(
  state: { error?: string },
  start: () => void,
  /** Clear a failure, which is what the button does while one is shown. */
  dismiss: () => void,
): ComposerVoice {
  return {
    status: state.error ? 'error' : 'idle',
    ...(state.error ? { error: state.error } : {}),
    micMuted: false,
    speakerMuted: false,
    level: 0,
    start,
    stop: dismiss,
    toggleMic: () => {},
    toggleSpeaker: () => {},
  }
}
